import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Menu, 
  Eye, 
  Edit, 
  Smartphone, 
  TrendingUp, 
  Settings, 
  LogOut, 
  User, 
  CreditCard, 
  Bell,
  ChevronDown,
  Sparkles,
  ArrowRight,
  Apple,
  Play,
  Image,
  Clock,
  AlertCircle,
  Crown,
  MapPin,
  Building2
} from 'lucide-react';
import Logo from '@/components/Logo';
import { subscriptionApi, getSubscriptionFeatures, type CurrentSubscription, type SubscriptionStatus } from '@/services/api/subscriptionApi';
import { restaurantApi } from '@/services/api/restaurantApi';
import { apiClient } from '@/services/api/apiClient';
import LocationSelectorModal from '@/components/dashboard/LocationSelectorModal';

const RestaurantDashboardWithSubscription = () => {
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<CurrentSubscription | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [activeMenu, setActiveMenu] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [hasMultipleLocations, setHasMultipleLocations] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [locationSelectorAction, setLocationSelectorAction] = useState<'edit' | 'preview'>('edit');
  const [locationSelectorAIMode, setLocationSelectorAIMode] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState<{
    dine_ai_recommendations: number;
    dine_ai_orders: number;
  } | null>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  
  // Get data from profile instead of hardcoding
  const restaurantName = profile?.restaurant_name || "Restaurant";
  const ownerName = profile?.owner_full_name || profile?.owner_first_name || "Owner";
  const location = profile ? `${profile.city}, ${profile.state}` : "";

  // Load subscription data and restaurant profile
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load both subscription and restaurant data in parallel
        const [status, profileData] = await Promise.all([
          subscriptionApi.getSubscriptionStatus(),
          restaurantApi.getProfile()
        ]);
        
        console.log('Loaded profile data:', profileData);
        console.log('Current location:', profileData.city, profileData.state);
        console.log('Current rest_id:', profileData.current_rest_id);
        
        setSubscriptionStatus(status);
        setProfile(profileData);
        
        if (status.has_subscription && status.subscription) {
          setSubscriptionData(status.subscription);
        }
        
        // Set active menu from profile if it exists
        if (profileData.active_menu) {
          setActiveMenu(profileData.active_menu);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);
  
  // Check for multiple locations when profile is loaded
  useEffect(() => {
    if (profile) {
      checkMultipleLocations();
    }
  }, [profile]);
  
  // Check if user has multiple locations
  const checkMultipleLocations = async () => {
    try {
      console.log('🔍 Checking multiple locations...');
      const response = await apiClient.get('/locations');
      console.log('📍 Locations API response:', response.data);
      const locationsList = Array.isArray(response.data) ? response.data : [];
      setLocations(locationsList);
      const hasMultiple = locationsList.length > 1;
      setHasMultipleLocations(hasMultiple);
      console.log('📍 Set hasMultipleLocations to:', hasMultiple, 'with', locationsList.length, 'locations');
    } catch (error) {
      console.error('Failed to check locations:', error);
    }
  };

  // Fetch dashboard metrics (Dine AI Recommendations and Orders)
  useEffect(() => {
    const fetchDashboardMetrics = async () => {
      try {
        const response = await apiClient.get('/analytics/dashboard-metrics');
        setDashboardMetrics(response.data);
      } catch (error) {
        console.error('[RestaurantDashboardWithSubscription] Failed to fetch dashboard metrics:', error);
        // Set defaults on error
        setDashboardMetrics({
          dine_ai_recommendations: 0,
          dine_ai_orders: 0
        });
      }
    };

    fetchDashboardMetrics();
  }, []);

  // Get subscription features for UI
  const subscriptionFeatures = getSubscriptionFeatures(subscriptionData);

  const handleEditMenu = () => {
    console.log('🔍 handleEditMenu called', {
      hasMultipleLocations,
      locations: locations.length
    });
    
    if (hasMultipleLocations) {
      // Show location selector for multiple locations
      setLocationSelectorAction('edit');
      setLocationSelectorAIMode(false);
      setShowLocationSelector(true);
    } else {
      // Single location - navigate directly
      if (activeMenu?.menu_id) {
        navigate(`/restaurant-menu-review?processing_id=${activeMenu.menu_id}`);
      } else {
        navigate('/restaurant-menu-upload');
      }
    }
  };

  const handlePreviewMenu = () => {
    if (hasMultipleLocations) {
      // Show location selector for multiple locations
      setLocationSelectorAction('preview');
      setLocationSelectorAIMode(false);
      setShowLocationSelector(true);
    } else {
      // Single location - navigate directly
      if (activeMenu?.menu_id) {
        navigate(`/menu-preview?menu_id=${activeMenu.menu_id}`);
      } else {
        navigate('/restaurant-menu-upload');
      }
    }
  };

  const handleLogout = () => {
    // Handle logout logic
    navigate('/restaurant-login');
  };
  
  const handleUpgrade = () => {
    navigate('/subscription-plans');
  };
  
  const handleGenerateAIImages = () => {
    if (hasMultipleLocations) {
      // Show location selector for multiple locations
      setLocationSelectorAction('edit');
      setLocationSelectorAIMode(true);
      setShowLocationSelector(true);
    } else {
      // Single location - navigate directly
      if (activeMenu?.menu_id) {
        navigate(`/restaurant-menu-review?processing_id=${activeMenu.menu_id}&aiMode=true`);
      } else {
        navigate('/restaurant-menu-upload');
      }
    }
  };

  const settingsMenuItems = [
    { icon: User, label: 'Profile', action: () => navigate('/restaurant-settings') },
    { icon: CreditCard, label: 'Subscription Plan', action: () => navigate('/restaurant-settings') },
    { icon: Settings, label: 'Settings', action: () => navigate('/restaurant-settings') },
    { icon: LogOut, label: 'Logout', action: handleLogout },
  ];

  // Render subscription status badge
  const renderSubscriptionBadge = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-400/20 rounded-lg border border-gray-400/30">
          <span className="text-gray-300 text-xs font-medium">Loading...</span>
        </div>
      );
    }

    if (error || !subscriptionData) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-red-400/20 rounded-lg border border-red-400/30">
          <AlertCircle className="w-3 h-3 text-red-300" />
          <span className="text-red-300 text-xs font-medium">No Subscription</span>
        </div>
      );
    }

    const { tier } = subscriptionFeatures;

    // Paid subscription
    const planColors = {
      'Yummy Plan': 'bg-green-400/20 border-green-400/30 text-green-300',
      'Delicious Plan': 'bg-blue-400/20 border-blue-400/30 text-blue-300',
      'Gourmet Plan': 'bg-purple-400/20 border-purple-400/30 text-purple-300',
    };

    const colorClass = planColors[tier as keyof typeof planColors] || 'bg-gray-400/20 border-gray-400/30 text-gray-300';

    return (
      <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${colorClass}`}>
        {tier === 'Gourmet Plan' && <Crown className="w-3 h-3" />}
        <span className="text-xs font-medium">{tier}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Enhanced Dashboard Header with subtle background */}
        <div className="bg-white/5 backdrop-blur-sm border-b border-white/10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="container mx-auto px-4 py-6"
          >
            <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Logo className="text-white h-8" />
              <div className="h-6 w-px bg-white/20"></div>
              
              {/* Restaurant Name and Location */}
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-white">{restaurantName}</h1>
                {hasMultipleLocations && (
                  <button
                    onClick={() => {
                      setLocationSelectorAction('switch');
                      setShowLocationSelector(true);
                    }}
                    className="flex items-center gap-2 px-2 py-1 bg-white/10 rounded-lg border border-white/20 hover:bg-white/20 transition-colors text-sm"
                  >
                    <MapPin className="w-3 h-3 text-yellow-400" />
                    <span className="text-white/80">
                      {location}
                    </span>
                    <ChevronDown className="w-3 h-3 text-white/60" />
                  </button>
                )}
                {!hasMultipleLocations && location && (
                  <span className="text-white/60 text-sm">{location}</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-white/60 hover:text-white cursor-pointer transition-colors" />
              
              <div className="relative">
                <button
                  ref={settingsButtonRef}
                  onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                  className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-medium">
                      {userInfo.first_name && userInfo.last_name 
                        ? `${userInfo.first_name[0]}${userInfo.last_name[0]}`.toUpperCase()
                        : userInfo.name 
                          ? userInfo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                          : 'U'
                      }
                    </div>
                    <span className="text-[10px] text-white/60 mt-0.5">
                      {subscriptionFeatures.tier === 'Delicious Plan' ? 'Delicious' : 
                       subscriptionFeatures.tier === 'Gourmet Plan' ? 'Gourmet' : 
                       subscriptionFeatures.tier === 'Yummy Plan' ? 'Yummy' : 'Free'}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-white/60" />
                </button>

                {isSettingsOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                  >
                    {settingsMenuItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            item.action();
                            setIsSettingsOpen(false);
                          }}
                          className="flex items-center gap-3 w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <item.icon className="w-4 h-4" />
                          <span className="text-sm">{item.label}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Content Area */}
        <div className="container mx-auto px-4">
          {/* Welcome Message with Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-10 mb-10"
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8">
              <h2 className="text-3xl font-bold text-white mb-2">
                Welcome back, {userInfo.first_name || ownerName.split(' ')[0]}!
              </h2>
              <p className="text-white/70 text-lg mb-6">Here's your restaurant overview</p>
              
              {/* Stats Bar */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/10">
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">{activeMenu?.total_items || 0}</p>
                  <p className="text-sm text-white/60 mt-1">Active Items</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-400">91%</p>
                  <p className="text-sm text-white/60 mt-1">Have Images</p>
                </div>
                <div className="text-center relative">
                  <span className="absolute top-0 right-0 text-white/40 text-xs">7d</span>
                  <p className="text-3xl font-bold text-yellow-400">
                    {dashboardMetrics?.dine_ai_recommendations ?? 0}
                  </p>
                  <p className="text-sm text-white/60 mt-1">Dine AI Recommendations</p>
                </div>
                <div className="text-center relative">
                  <span className="absolute top-0 right-0 text-white/40 text-xs">7d</span>
                  <p className="text-3xl font-bold text-yellow-400">
                    {dashboardMetrics?.dine_ai_orders ?? 0}
                  </p>
                  <p className="text-sm text-white/60 mt-1">Dine AI Orders</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Primary Actions Section */}
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-6">Menu Management</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Edit Menu - Primary Action Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-white/15 to-white/10 backdrop-blur-md rounded-xl border border-white/20 p-8 hover:from-white/20 hover:to-white/15 transition-all cursor-pointer shadow-xl hover:shadow-2xl"
                onClick={handleEditMenu}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="bg-yellow-400/20 p-4 rounded-xl">
                    <Edit className="w-8 h-8 text-yellow-400" />
                  </div>
                  {subscriptionFeatures.hasMenuManagement && (
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  )}
                </div>
                <h3 className="text-white font-bold text-xl mb-3">
                  Edit Menu
                </h3>
                <p className="text-white/80 mb-4">{activeMenu?.total_items || 0} items ready to manage</p>
                <p className="text-white/60 text-sm mb-6">Update menu items, prices, descriptions, and images</p>
                <div className="flex items-center text-yellow-400 font-medium">
                  <span>Make changes</span>
                  <ArrowRight className="w-5 h-5 ml-auto" />
                </div>
              </motion.div>

              {/* Preview Menu - Primary Action Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-white/15 to-white/10 backdrop-blur-md rounded-xl border border-white/20 p-8 hover:from-white/20 hover:to-white/15 transition-all cursor-pointer shadow-xl hover:shadow-2xl"
                onClick={handlePreviewMenu}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="bg-blue-400/20 p-4 rounded-xl">
                    <Eye className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <h3 className="text-white font-bold text-xl mb-3">Preview Menu</h3>
                <p className="text-white/80 mb-4">Live customer view</p>
                <p className="text-white/60 text-sm mb-6">See exactly how your menu appears to customers</p>
                <div className="flex items-center text-blue-400 font-medium">
                  <span>View menu</span>
                  <ArrowRight className="w-5 h-5 ml-auto" />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Secondary Actions Grid */}
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-6">Features & Tools</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

              {/* Analytics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 transition-all shadow-lg hover:shadow-xl ${
                  subscriptionFeatures.hasAnalytics 
                    ? 'hover:bg-white/15 cursor-pointer' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white/20 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              {subscriptionFeatures.hasAnalytics ? (
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              ) : (
                <div className="bg-yellow-400/20 text-yellow-300 text-xs px-2 py-1 rounded">
                  Upgrade
                </div>
              )}
            </div>
            <h3 className="text-white font-semibold mb-2">Analytics</h3>
            <p className="text-white/60 text-sm mb-3">
              {subscriptionFeatures.hasAnalytics 
                ? 'Track menu performance and insights'
                : 'Requires Delicious or Gourmet plan'
              }
            </p>
            <div className="flex items-center text-white/80 text-sm">
              <span>{subscriptionFeatures.hasAnalytics ? 'View insights' : 'Locked'}</span>
              <ArrowRight className="w-4 h-4 ml-auto" />
            </div>
          </motion.div>

              {/* AI Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className={`bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 transition-all shadow-lg hover:shadow-xl ${
                  subscriptionFeatures.hasAIImages 
                    ? 'hover:bg-white/15 cursor-pointer' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={subscriptionFeatures.hasAIImages ? handleGenerateAIImages : undefined}
              >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white/20 p-3 rounded-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              {subscriptionFeatures.hasAIImages ? (
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              ) : (
                <div className="bg-yellow-400/20 text-yellow-300 text-xs px-2 py-1 rounded">
                  Upgrade
                </div>
              )}
            </div>
            <h3 className="text-white font-semibold mb-2">AI Images</h3>
            <p className="text-white/60 text-sm mb-3">
              {subscriptionFeatures.hasAIImages 
                ? 'Generate stunning food images'
                : 'Requires Delicious or Gourmet plan'
              }
            </p>
            <div className="flex items-center text-white/80 text-sm">
              <span>{subscriptionFeatures.hasAIImages ? 'Generate' : 'Locked'}</span>
              <ArrowRight className="w-4 h-4 ml-auto" />
            </div>
              </motion.div>
            </div>
          </div>

          {/* Removed trial status card - trials no longer supported */}

        {/* Feature Access Summary */}
        {!loading && subscriptionData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6"
          >
            <h3 className="text-white font-semibold mb-4">Your Plan Features</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${subscriptionFeatures.hasMenuAssistant ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="text-white/80 text-sm">Menu Assistant</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${subscriptionFeatures.hasPOSIntegration ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="text-white/80 text-sm">POS Integration</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${subscriptionFeatures.hasAnalytics ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="text-white/80 text-sm">Analytics</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${subscriptionFeatures.hasAIImages ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="text-white/80 text-sm">AI Images</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${subscriptionFeatures.hasAutoPromotions ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="text-white/80 text-sm">Auto Promotions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${subscriptionFeatures.hasAdvancedInsights ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="text-white/80 text-sm">Advanced Insights</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${subscriptionFeatures.hasAPIAccess ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="text-white/80 text-sm">API Access</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${subscriptionFeatures.hasWhiteLabel ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className="text-white/80 text-sm">White Label</span>
              </div>
            </div>
          </motion.div>
        )}
        </div>
      </div>
      
      {/* Location Selector Modal */}
      <LocationSelectorModal
        isOpen={showLocationSelector}
        onClose={() => setShowLocationSelector(false)}
        action={locationSelectorAction as 'edit' | 'preview' | 'switch'}
        aiMode={locationSelectorAIMode}
        onLocationSwitched={async () => {
          // Refresh the data after location switch
          console.log('🔄 Location switched, refreshing data...');
          setLoading(true);
          try {
            // Fetch updated profile data
            const [status, profileData] = await Promise.all([
              subscriptionApi.getSubscriptionStatus(),
              restaurantApi.getProfile()
            ]);
            
            console.log('Refreshed profile data:', profileData);
            console.log('New location:', profileData.city, profileData.state);
            
            setSubscriptionStatus(status);
            setProfile(profileData);
            
            if (status.has_subscription && status.subscription) {
              setSubscriptionData(status.subscription);
            }
            
            // Set active menu from profile if it exists
            if (profileData.active_menu) {
              setActiveMenu(profileData.active_menu);
            }
            
            // Refresh locations
            await checkMultipleLocations();
          } catch (error) {
            console.error('Failed to refresh data after location switch:', error);
          } finally {
            setLoading(false);
          }
        }}
      />
      
      {/* Footer with Legal Links */}
      <div className="mt-12 pb-6 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-white/60 text-sm">
              © 2025 Dine AI LLC. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a 
                href="/terms" 
                target="_blank"
                className="text-white/60 hover:text-dineai-yellow transition-colors"
              >
                Terms of Service
              </a>
              <a 
                href="/privacy" 
                target="_blank"
                className="text-white/60 hover:text-dineai-yellow transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="/cookies" 
                target="_blank"
                className="text-white/60 hover:text-dineai-yellow transition-colors"
              >
                Cookie Policy
              </a>
              <a 
                href="/accessibility" 
                target="_blank"
                className="text-white/60 hover:text-dineai-yellow transition-colors"
              >
                Accessibility
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantDashboardWithSubscription;